import { createContext, useContext, useState, useCallback, type ReactNode } from "react";
import type { Tool } from "./tools-data";

/**
 * The mechanism that makes a GENERIC multi-step pipeline possible without
 * touching each of the 200+ individual tool components. Every tool
 * already uses the same two shared components for file in/out --
 * FileDropZone (components/ToolLayout.tsx) and DownloadButton (same
 * file). Making exactly those two components "pipeline-aware" (see
 * their own updated code) is enough: when a PipelineProvider is active,
 * FileDropZone offers "use the previous step's output" as one more way
 * to supply a file (on top of drag/drop/browse, unchanged), and
 * DownloadButton offers "continue to the next step" as one more action
 * (on top of Download/Share/Copy, unchanged). No tool's own logic
 * changes; neither component behaves any differently when there's no
 * active pipeline.
 */

/**
 * Name of the file a bundle run ends with: the person's own file name plus the bundle, instead of every
 * step's prefix stacked up ("compressed_normalized_denoised_voice.mp3" becomes "voice-podcast-cleanup.mp3").
 * The extension is the one the last step produced. Without an original name the produced name is kept.
 */
export function bundleFinalName(originalName: string | null | undefined, bundleId: string | null | undefined, producedName: string): string {
  if (!originalName || !bundleId) return producedName;
  const stem = originalName.replace(/\.[^./\\]+$/, "").replace(/[\\/:*?"<>|\u0000-\u001f]+/g, "-").replace(/-+$/, "").trim();
  if (!stem) return producedName;
  const ext = producedName.match(/\.([A-Za-z0-9]{1,8})$/)?.[1];
  return `${stem}-${bundleId}${ext ? `.${ext}` : ""}`;
}

export interface PipelineStep {
  tool: Tool;
}

interface PipelineState {
  steps: PipelineStep[];
  currentStepIndex: number;
  /** The previous step's output, ready to be picked up by the current
   * step's FileDropZone. Null for step 1 (nothing to hand off yet) and
   * briefly null again right after advancing, until the new current
   * step produces its own output. */
  handoffFile: { blob: Blob; fileName: string } | null;
  /** Called by DownloadButton when the current step's tool produces a
   * real result -- stores it as the next step's handoff and advances
   * the stepper. A no-op past the last step. */
  provideStepOutput: (blob: Blob, fileName: string) => void;
  /** Moves to the next step without using this one; the file from the last finished step stays available. */
  skipStep: () => void;
  /** What each step produced (step index -> file name and size), for the size summary. */
  outputs: Record<number, { fileName: string; size: number }>;
  /** Called by DownloadButton whenever the current step has a result, including on the last step. */
  recordOutput: (stepIndex: number, fileName: string, size: number, blob?: Blob) => void;
  /** Steps the person skipped (shown as "skipped", not as done). */
  skipped: ReadonlySet<number>;
  /** True once the person ended the run from the last step with "Skip and finish". */
  finished: boolean;
  /** Ends the run on the last step without using it; the latest file is then offered for download. */
  finishRun: () => void;
  /** The newest file the run has: the last step's result if it made one, else the latest earlier result. Null if no step made a file. */
  finalFile: { blob: Blob; fileName: string } | null;
  /** Remembers the name of the file the person started the run with (called by the first step's file box). */
  setOriginalName: (name: string) => void;
  /** The name to download the run's final file under (the start file's name plus the bundle). */
  finalName: (producedName: string) => string;
}

const PipelineContext = createContext<PipelineState | null>(null);

/** Returns null outside a pipeline -- every consumer (FileDropZone,
 * DownloadButton) must treat null as "behave exactly as before." */
export function usePipelineContext(): PipelineState | null {
  return useContext(PipelineContext);
}

export function PipelineProvider({ tools, bundleId = null, children }: { tools: Tool[]; bundleId?: string | null; children: ReactNode }) {
  const [originalName, setOriginalName] = useState<string | null>(null);
  const finalName = useCallback((produced: string) => bundleFinalName(originalName, bundleId, produced), [originalName, bundleId]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [handoffFile, setHandoffFile] = useState<{ blob: Blob; fileName: string } | null>(null);

  const provideStepOutput = useCallback(
    (blob: Blob, fileName: string) => {
      setCurrentStepIndex((i) => {
        if (i >= tools.length - 1) return i; // already on the last step, nothing to advance to
        setHandoffFile({ blob, fileName });
        return i + 1;
      });
    },
    [tools.length]
  );

  const [outputs, setOutputs] = useState<Record<number, { fileName: string; size: number }>>({});
  const [lastStepFile, setLastStepFile] = useState<{ blob: Blob; fileName: string } | null>(null);
  const recordOutput = useCallback((stepIndex: number, fileName: string, size: number, blob?: Blob) => {
    setOutputs((o) => (o[stepIndex]?.fileName === fileName && o[stepIndex]?.size === size ? o : { ...o, [stepIndex]: { fileName, size } }));
    if (blob && stepIndex === tools.length - 1) setLastStepFile({ blob, fileName });
  }, [tools.length]);
  const [skipped, setSkipped] = useState<ReadonlySet<number>>(new Set());
  const [finished, setFinished] = useState(false);
  const skipStep = useCallback(() => {
    setCurrentStepIndex((i) => {
      if (i >= tools.length - 1) return i;
      setSkipped((s) => new Set(s).add(i));
      return i + 1;
    });
  }, [tools.length]);
  const finishRun = useCallback(() => {
    setSkipped((s) => (lastStepFile ? s : new Set(s).add(tools.length - 1)));
    setFinished(true);
  }, [tools.length, lastStepFile]);
  const finalFile = lastStepFile ?? handoffFile;

  const steps: PipelineStep[] = tools.map((tool) => ({ tool }));

  return (
    <PipelineContext.Provider value={{ steps, currentStepIndex, handoffFile, provideStepOutput, skipStep, outputs, recordOutput, skipped, finished, finishRun, finalFile, setOriginalName, finalName }}>
      {children}
    </PipelineContext.Provider>
  );
}
